




















































































































import { Vue, Component, Watch } from "vue-property-decorator";

import router from "@/router/router";
import { DevicesModule } from "@/store/modules/DevicesModule";

import ClientPanel from "../../data/models/ClientPanel";
import {
  CATEGORY_HY_DRY_ID,
  CATEGORY_COM_BOX_ID,
  getCategoryIdFromSerialNumber,
  isClientPanel,
  isIresCategory,
  getUpdatedCategoryAndTemplateIds
} from '@/data/models/DeviceSettingsManager'

import DeviceCategoryItem from "./DeviceCategoryItem.vue";
import { observeDeviceOverviews } from "@/data/repository/AllDevicesRepository";
import { 
  getNumber, 
  setNumber,
} from "@/utils/SecureLocalStorage";
import { filterRetainedDevices, getNumberOfRetainedDevices } from "@/utils/DeviceManagementUtils";

import { StateChanger } from "vue-infinite-loading";
import InfiniteLoading from "vue-infinite-loading";
import { observeClientPanels } from "@/data/repository/ClientPanelRepository";
import { DeviceOverview } from "@/data/models/DeviceOverview";
import { 
  findBranch, 
  getClientId, 
  hasAdministrationCapabilities, 
  hasClientEnabledStrictBranchFilters, 
  shouldHideSerialNumber 
} from "@/data/repository/ClientRepository";
import { BranchFilter } from "@/data/models/BranchFilter";
import { GenericDeviceFilter, getGenericFilterName, matchesGenericDeviceFilter } from "@/data/models/DeviceFilter";
import { getDefaultBranchFilterOfUser, isActiveUserIresAdminAccount } from "@/data/repository/UserRepository";
import { getBranchName } from "@/data/models/Branch";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import MenuIconWithSelectionListComponent from "../common/FilterMenu.vue";
import SearchForDeviceComponent from "../devices/SearchForDevice.vue";
import BranchFilterSelectorComponent from "../common/BranchFilterSelector.vue";
import { DeviceCategoriesAndTemplatesModule } from "@/store/modules/DeviceCategoriesAndTemplatesModule";
import { defineCategoriesAndTemplatesClientAccess } from "@/permissions/ClientPermissions";
import { calculateBackgroundStyle, setLastVisitedItem, StorageKeyLastVisitedItemLevel } from "@/utils/Utils";
import { COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { themed } from "@/utils/Theme";
import { getStoredBranchFilterId, setStoredBranchFilterId, removeStoredBranchFilterId } from "@/utils/BranchFilterStorage";

const ALL_DEVICES_CATEGORY_ID = "ALL_DEVICES_CATEGORY";

@Component({
  components: {
    "branch-filter-selector": BranchFilterSelectorComponent,
    "device-category-item": DeviceCategoryItem,
    "infinite-loading": InfiniteLoading,
    "menu-icon": MenuIconWithSelectionListComponent,
    "search-for-device": SearchForDeviceComponent,
  },
})

export default class AllDevices extends Vue {
  allDevices: ClientPanel[] = [];
  allFilters = [
    GenericDeviceFilter.ALL_DEVICES,
    GenericDeviceFilter.IN_USAGE,
    GenericDeviceFilter.AVAILABLE,
    GenericDeviceFilter.NOT_AVAILABLE,
  ];
  clientId = getClientId();
  componentActive = false;
  hasNoDevices = true;
  infiniteId = new Date();
  pageSize = 20;
  priorityIds = [CATEGORY_COM_BOX_ID, CATEGORY_HY_DRY_ID];
  refreshTimer!: NodeJS.Timeout;
  refreshTrigger = 0;
  searchQuery: string | null = null;
  searchTimeout: NodeJS.Timeout | null = null;
  selectedBranchFilter = getDefaultBranchFilterOfUser();
  selectedDeviceFilter = GenericDeviceFilter.ALL_DEVICES;
  sliceLength = this.pageSize;
  storageKeyAllDevicesFilter = "storageKeyAllDevicesFilter";
  storageKeyBranchFilter = "AllDevicesBranchFilter"
  storageKeySliceLength = "allDevicesStorageKeySliceLength";
  subscriptions: Array<() => void> = [];
  windowWidth = 0;

  retrievedClientPanels: ClientPanel[] = [];
  retrievedDeviceOverviews: DeviceOverview[] = []; 

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  get allDevicesCategoryId() {
    return ALL_DEVICES_CATEGORY_ID;
  }

  get categoriesToDisplay() {
    return this.sliceCategoriesToDisplay(this.sliceLength);
  }

  get clientPanels() {
    return this.retrievedClientPanels;
  }

  get deviceCategories() {
    const priorityIds = this.priorityIds;
    const deviceSettingsJson = this.deviceSettingsJson;

    return deviceSettingsJson 
      ? deviceSettingsJson.deviceCategories.sort((a, b) => {
          if (priorityIds.includes(a.id) && priorityIds.includes(b.id)) {
            return priorityIds.indexOf(a.id) - priorityIds.indexOf(b.id);
          } else if (priorityIds.includes(a.id)) {
            return -1;
          } else if (priorityIds.includes(b.id)) {
            return 1;
          } else {
            return a.categoryName.localeCompare(b.categoryName);
          }
        })
      : [];
  }

  get deviceFilterIconRes() {
    return "mdi-filter-variant";
  }

  get deviceOverviews() {
    return this.retrievedDeviceOverviews;
  }

  /**
   * Iterates over each device overview, updating the category and template IDs based on shared device settings.
   * This method leverages the `getUpdatedCategoryAndTemplateIds` function to find the updated category and template IDs
   * from the shared device settings that correspond to the local settings for each device. It then updates these IDs
   * in the device overview list.
   *
   * @returns {DeviceOverview[]} An array of device overview objects with updated category and template IDs.
   */
  get deviceOverviewsWithUpdatedCategoryAndTemplateIds() {
    const deviceOverviews = this.deviceOverviews;

    deviceOverviews.forEach((device) => {
      const { updatedCategoryId, updatedTemplateId } = getUpdatedCategoryAndTemplateIds(
        device.categoryId,
        device.templateId,
        this.localDeviceSettingsJson,
        this.sharedDeviceSettingsJson,
        device.devicePowerUsageType ?? undefined,
      );

      device.categoryId = updatedCategoryId;
      device.templateId = updatedTemplateId;
    });

    return deviceOverviews;
  }

  get isDefaultBranchFilterSelected() {
    return this.selectedBranchFilter.isDefaultBranchFilter();
  }

  get isDefaultDeviceFilterSelected() {
    return this.selectedDeviceFilter === GenericDeviceFilter.ALL_DEVICES;
  }

  get isLoadingDevices() {
    return !(this.clientPanels.length > 0 ||
      this.deviceOverviews.length > 0 ||
      this.deviceSettingsJson);
  }

  get showInfiniteLoading() {
    return this.deviceCategories.length > 0;
  }

  get selectedDeviceFiltersDescription() {
    let filtersName = this.$t("allDevices.title").toString();
    let isFilterEnabled = false;

    // Might use later
    /* const selectedClientFilterName = this.selectedClientFilter.client
      ? getClientName(this.selectedClientFilter.client)
      : this.$t("common.all");

    if (this.isAllowedToViewClientSelector && selectedClientFilterName !== this.$t("common.all")) {
      filtersName += " (" + selectedClientFilterName;
      isFilterEnabled = true;
    } */

    const selectedBranchFilterName = this.selectedBranchFilter.branch
      ? getBranchName(this.selectedBranchFilter.branch)
      : this.$t("common.all");

    if (selectedBranchFilterName !== this.$t("common.all")) {
      filtersName += !isFilterEnabled
        ? " ("
        : "";
      filtersName += /* (this.isAllowedToViewClientSelector
        ? ": "
        : "") +  */selectedBranchFilterName;
      isFilterEnabled = true;
    }

    return filtersName + (isFilterEnabled ? ")" : "");
  }

  @Watch("selectedBranchFilter")
  onBranchFilterChanged() {
    this.resetDisplayedCategories();
    this.storeBranch();
  }

  @Watch("selectedDeviceFilter")
  onFilterChanged(newFilter: GenericDeviceFilter) {
    this.setToolbarTitle(newFilter);
    this.resetDisplayedCategories();

    setNumber(this.storageKeyAllDevicesFilter, this.selectedDeviceFilter);
  }

  get isAllowedToCopyAllDevicesToClientsAdministration() {
    return hasAdministrationCapabilities() && isActiveUserIresAdminAccount();
  }

  async infiniteHandler(data: StateChanger) {
    const nextSliceLength = this.sliceLength + this.pageSize;
    const nextSlice = this.sliceCategoriesToDisplay(nextSliceLength);

    this.sliceLength = nextSliceLength;

    if (nextSliceLength > nextSlice.length) {
      data.complete();
    } else {
      data.loaded();
    }
  }

  get isAllowedToDefineOwnDeviceCategoriesAndTemplates() {
    return defineCategoriesAndTemplatesClientAccess();
  }

  get sharedDeviceSettingsJson() {
    return DeviceCategoriesAndTemplatesModule.sharedDeviceCategoriesAndTemplates;
  }

  get localDeviceSettingsJson() {
    return DeviceCategoriesAndTemplatesModule.localDeviceCategoriesAndTemplates;
  }

  get deviceSettingsJson() {
    return this.isAllowedToDefineOwnDeviceCategoriesAndTemplates
      ? this.localDeviceSettingsJson
      : this.sharedDeviceSettingsJson;
  }

  get shouldHideSerialNumber() {
    return shouldHideSerialNumber();
  }

  clearSearchQuery() {
    this.searchQuery = null;
    this.resetDisplayedCategories();
  }

  debounceSearch(newQuery: string) {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    if (newQuery === "") {
      this.clearSearchQuery();
      return;
    }

    this.searchTimeout = setTimeout(() => {
      this.processSearchQuery(newQuery);
    }, 400);
  }

  filterDevices(devices: (DeviceOverview | ClientPanel)[]) {
    const filter = this.selectedDeviceFilter;
    const branchFilter = this.selectedBranchFilter;
    const searchQuery = this.searchQuery?.toUpperCase();

    const result = devices.filter((device: DeviceOverview | ClientPanel) =>
      (
        branchFilter.doesMatchGenericDevice(device) ||
        this.isDefaultBranchFilterSelected
      ) &&
      matchesGenericDeviceFilter(
        filter,
        device
      )
    );

    if (searchQuery) {
      const searchQueryLowerCase = searchQuery.toLowerCase();

      const searchedDevices = result.filter(
        (device: DeviceOverview | ClientPanel) => {
          if (isClientPanel(device)) {
            const panel = device as ClientPanel;

            return (
                panel.serialNumber
                  .toLowerCase()
                  .includes(searchQueryLowerCase) &&
                !this.shouldHideSerialNumber
              ) ||
              panel.inventoryNumber
                ?.toLowerCase()
                .includes(searchQueryLowerCase)
          }
          else {
            const deviceOverview = device as DeviceOverview;

            return deviceOverview.serialNumber
                ?.toLowerCase()
                .includes(searchQueryLowerCase) ||
              deviceOverview.qrCode
                ?.toLowerCase()
                .includes(searchQueryLowerCase) ||
              deviceOverview.inventoryNumber
                ?.toLowerCase()
                .includes(searchQueryLowerCase)
          }
        });

        if (this.hasNoDevices !== false) {
          this.hasNoDevices = searchedDevices.length === 0;
        }

        return searchedDevices;   
    }

    const retainedDevices = filterRetainedDevices(result);

    if (this.hasNoDevices !== false) {
      this.hasNoDevices = retainedDevices.length === 0;
    }

    return retainedDevices;
  }

  getAllDevices() {
    const allDevices: (DeviceOverview | ClientPanel)[] = [
      ...this.clientPanels,
      ...this.deviceOverviews
    ];

    return allDevices;
  }

  getAllFiltersName() {
    let filtersName = this.getGenericFilterName(this.selectedDeviceFilter);

    const selectedBranchFilterName = this.selectedBranchFilter.branch
      ? getBranchName(this.selectedBranchFilter.branch)
      : this.$t("common.all");

    if (selectedBranchFilterName !== this.$t("common.all")) {
      filtersName += ", " + selectedBranchFilterName;
    }

    return filtersName;
  }

  calculateBackgroundStyle(itemId: string) {
    return calculateBackgroundStyle(itemId);
  }

  getDevicesWithCategory(deviceCategoryId: string) {
    if (isIresCategory(deviceCategoryId)) {
      const clientPanelsFilteredWithCategory = this.clientPanels.filter((panel) => {
        return getCategoryIdFromSerialNumber(panel.serialNumber) === deviceCategoryId;
      });

      return clientPanelsFilteredWithCategory;
    }

    const deviceOverviews = this.deviceOverviewsWithUpdatedCategoryAndTemplateIds;
    const devicesFilteredByCategory = deviceOverviews.filter((device) => device.categoryId === deviceCategoryId);

    return devicesFilteredByCategory;
  }

  getGenericFilterName(deviceFilter: GenericDeviceFilter) {
    return getGenericFilterName(this, deviceFilter);
  }

  getNoOfTotalDevices(devices: (DeviceOverview | ClientPanel)[]) {
    return getNumberOfRetainedDevices(devices);
  }

  onOpenAllDeviceCategories(shouldFocusSearch?: boolean) {
    DevicesModule.setSelectedCategories(this.categoriesToDisplay);

    setLastVisitedItem(
      ALL_DEVICES_CATEGORY_ID,
      StorageKeyLastVisitedItemLevel.LEVEL_1, 
    );

    router.push({
      path: `allDevices/all`,
      query: shouldFocusSearch ? {
        search: "true"
      } : {}
    });
  }

  onOpenDeviceCategory(categoryId: string, index: number) {
    setNumber(
      this.storageKeySliceLength,
      this.pageSize + Math.floor(index / this.pageSize) * this.pageSize
    );
    
    setLastVisitedItem(
      categoryId,
      StorageKeyLastVisitedItemLevel.LEVEL_1, 
    );

    const selectedCategory = this.deviceCategories.filter((category) => { return category.id === categoryId });
    DevicesModule.setSelectedCategories(selectedCategory);

    router.push({
      path: `allDevices/${categoryId}`
    });
  }

  processSearchQuery(newQuery: string) {
    this.searchQuery = newQuery;
    this.resetDisplayedCategories();
  }

  resetDisplayedCategories() {
    this.hasNoDevices = true;
    this.sliceLength = this.pageSize;
    this.infiniteId = new Date();
  }

  storeBranch() {
    if (this.selectedBranchFilter.branch?.id) {
      setStoredBranchFilterId(this.storageKeyBranchFilter, this.selectedBranchFilter.branch.id);
    }
    else {
      removeStoredBranchFilterId(this.storageKeyBranchFilter);
    }
  }

  setToolbarTitle(newFilter: GenericDeviceFilter) {
    const devicesString = this.selectedDeviceFilter === GenericDeviceFilter.ALL_DEVICES
      ? `${this.$t("pages.allDevices").toString()}`
      : `${this.$t("pages.allDevices").toString()} (${this.getGenericFilterName(newFilter)})`;

    ToolbarContentModule.setToolbarTitle(devicesString);
  }

  sliceCategoriesToDisplay(length: number) {
    const deviceCategories = this.deviceCategories;

    return deviceCategories.slice(0, length);
  }

  startRefreshTimer() {
    this.refreshTimer = setInterval(() => {
      this.refreshTrigger++;
    }, 60 * 1000);
  }

  activated() {
    this.componentActive = true;
    this.setToolbarTitle(this.selectedDeviceFilter);
  }

  beforeDestroy() {
    this.subscriptions.forEach((subscription) => subscription());
    clearInterval(this.refreshTimer);
  }

  created() {
    localStorage.removeItem("CurrentOpenedDevice");
    localStorage.removeItem("IsReadOnly");
    localStorage.removeItem("SearchQuery");
    
    this.startRefreshTimer();
    
    this.subscriptions.push(observeDeviceOverviews(
      (deviceOverview: Array<DeviceOverview>) => {
        this.retrievedDeviceOverviews = deviceOverview;
      }
    ));
    this.subscriptions.push(observeClientPanels(
      (clientPanels: Array<ClientPanel>) => {
        this.retrievedClientPanels = clientPanels;
      }
    ));

    const cachedDeviceFilter =
      getNumber(this.storageKeyAllDevicesFilter) ?? GenericDeviceFilter.ALL_DEVICES;

    if (cachedDeviceFilter != null && cachedDeviceFilter !== undefined) {
      this.selectedDeviceFilter = cachedDeviceFilter;
    }

    const cachedSliceLenth = getNumber(this.storageKeySliceLength);
    if (cachedSliceLenth) {
      this.sliceLength = cachedSliceLenth;
      setNumber(this.storageKeySliceLength);
    }

    this.setToolbarTitle(this.selectedDeviceFilter);
  }

  deactivated() {
    this.componentActive = false;
  }

  mounted() {
    const branchIdQuery = getStoredBranchFilterId(this.storageKeyBranchFilter);

    if (branchIdQuery) {
      const matchingBranch = findBranch(branchIdQuery);

      if (matchingBranch) {
        this.selectedBranchFilter = new BranchFilter(
          hasClientEnabledStrictBranchFilters(),
          matchingBranch
        );
      }
    }

    this.componentActive = true;
  }
}
